import { stringToColor } from '@mahawi/eshop-common/src/string-to-color';

const getContrastColor = (hex: string): string => {
  const r: number = parseInt(hex.slice(1, 3), 16);
  const g: number = parseInt(hex.slice(3, 5), 16);
  const b: number = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? '#000000' : '#ffffff';
};
import {
  EFeatureFlag,
  type IFeatureFlag,
  type IProductFeatureFlag,
  type IWarehouseDetail,
} from '@mahawi/eshop-common/src/types';
import { Tag, Tooltip } from 'antd';
import React from 'react';

import { type IFeatureFlagsState } from '../reducers/feature-flags/types';
import { type IWarehouseState } from '../reducers/warehouse/types';
import { EMPTY_VALUE } from './empty-value';

const getFeatureFlagElement = ({
  code,
  ffColor,
  ff,
  productUUID,
  productFeatureFlag,
  onClose,
}: {
  code: EFeatureFlag;
  ffColor: string;
  ff: IFeatureFlag;
  productUUID?: string;
  productFeatureFlag: IProductFeatureFlag;
  onClose?: (
    productUUID: string,
    productFeatureFlag: IProductFeatureFlag,
  ) => void;
}): React.ReactElement => {
  return (
    <Tag
      key={code}
      style={{
        backgroundColor: ffColor,
        borderColor: ffColor,
        color: getContrastColor(ffColor),
      }}
      closable={
        !!onClose &&
        (productFeatureFlag.isVirtual === undefined ||
          productFeatureFlag.isVirtual === false)
      }
      onClose={(): void =>
        !!onClose && productUUID
          ? onClose(productUUID, productFeatureFlag)
          : undefined
      }
    >
      {ff.name}
    </Tag>
  );
};

export const featureFlagTags = ({
  productFeatureFlags,
  FeatureFlags,
  productUUID,
  onClose,
  Warehouse,
}: {
  productFeatureFlags: IProductFeatureFlag[];
  FeatureFlags: IFeatureFlagsState;
  productUUID?: string;
  Warehouse: IWarehouseState;
  onClose?: (
    productUUID: string,
    productFeatureFlag: IProductFeatureFlag,
  ) => void;
}): React.ReactElement[] => {
  const featureFlagElements: React.ReactElement[] = productFeatureFlags.map(
    (productFeatureFlag: IProductFeatureFlag): React.ReactElement => {
      const code: EFeatureFlag = productFeatureFlag.code;
      const ff: IFeatureFlag | undefined = FeatureFlags.featureFlags?.find(
        (f: IFeatureFlag): boolean => f.code === code,
      );

      if (!ff) {
        return <Tag key={code}>{code}</Tag>;
      }

      const ffColor: string = ff.color || stringToColor(ff.name);

      if (productFeatureFlag.value) {
        return (
          <Tooltip
            key={code}
            title={(): React.ReactElement => {
              const items: string[] = [];

              if (productFeatureFlag.value?.languageTypesCodes) {
                items.push(
                  `Language types codes: ${productFeatureFlag.value?.languageTypesCodes?.join(', ') || ''}`,
                );
              }

              if (productFeatureFlag.value?.warehouse) {
                const warehouse: IWarehouseDetail | undefined =
                  Warehouse.warehouses?.find(
                    (w: IWarehouseDetail): boolean =>
                      w.uuid === productFeatureFlag.value?.warehouse?.uuid,
                  );

                items.push(
                  `Warehouse: ${warehouse?.name || productFeatureFlag.value?.warehouse.uuid || EMPTY_VALUE}`,
                );
              }

              if (productFeatureFlag.value?.messages) {
                productFeatureFlag.value?.messages.forEach(
                  (message: string): void => {
                    items.push(`Message: ${message}`);
                  },
                );
              }

              return (
                <div>
                  {items.map((item: string, i: number): React.ReactElement => (
                    <div key={i}>{item}</div>
                  ))}
                </div>
              );
            }}
            placement="bottom"
          >
            {getFeatureFlagElement({
              code,
              ffColor,
              ff,
              productUUID,
              productFeatureFlag,
              onClose,
            })}
          </Tooltip>
        );
      }

      return getFeatureFlagElement({
        code,
        ffColor,
        ff,
        productUUID,
        productFeatureFlag,
        onClose,
      });
    },
  );

  return featureFlagElements;
};
